import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { environment } from 'src/app/environments/environment';

enum DashboardMappingStatus {
  Active = 1
}

interface AnalyticsIntegrationResponse {
  id: number;
  status: number;
  permissionGroupName: string;
  collectionName: string;
}

interface DashboardMappingResponse {
  id: number;
  dashboardId: string;
  menuName: string;
  sortOrder: number;
  isEnabled: boolean;
  status: DashboardMappingStatus;
  permissionKey: string;
}

interface DashboardEmbedResponse {
  dashboardId: string;
  embedUrl: string;
}

export interface AnalyticsMenuItem {
  id: number;
  menuName: string;
  sortOrder: number;
  link: string;
}

export interface DashboardMappingItem {
  id: number;
  dashboardId: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsMenuService {
  private readonly apiUrl = environment.companyApiBaseUrl;
  private readonly menuItemsSubject = new BehaviorSubject<AnalyticsMenuItem[]>(
    []
  );

  private loaded = false;
  private loadedCompanyId: number | null = null;
  private loadedAppId: number | null = null;

  constructor(
    private http: HttpClient,
    private permissionsService: NgxPermissionsService
  ) {}

  getMenuItems() {
    return this.menuItemsSubject.asObservable();
  }

  async loadMenuItems(
    companyId: number,
    appId: number,
    force = false
  ): Promise<void> {
    if (
      !force &&
      this.loaded &&
      this.loadedCompanyId === companyId &&
      this.loadedAppId === appId
    ) {
      return;
    }

    try {
      const integration = await firstValueFrom(
        this.http.get<AnalyticsIntegrationResponse>(
          `${this.apiUrl}/Companies/${companyId}/Analytics/integration`
        )
      );

      const isPlaceholder =
        integration.id <= 0 &&
        integration.status === 0 &&
        !integration.permissionGroupName?.trim() &&
        !integration.collectionName?.trim();

      if (isPlaceholder) {
        this.setMenuItems([], companyId, appId);
        return;
      }

      const mappings = await firstValueFrom(
        this.http.get<DashboardMappingResponse[]>(
          `${this.apiUrl}/Companies/${companyId}/Analytics/dashboards?appId=${appId}`
        )
      );

      const userPermissions = Object.keys(
        this.permissionsService.getPermissions() || {}
      );
      const items = mappings
        .filter(
          (mapping) =>
            mapping.isEnabled &&
            mapping.status === DashboardMappingStatus.Active
        )
        .filter(
          (mapping) =>
            !mapping.permissionKey ||
            userPermissions.includes(mapping.permissionKey)
        )
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .map((mapping) => ({
          id: mapping.id,
          menuName: mapping.menuName,
          sortOrder: mapping.sortOrder,
          link: `/admin/home/analytics/${mapping.id}`
        }));

      this.setMenuItems(items, companyId, appId);
    } catch {
      this.setMenuItems([], companyId, appId);
    }
  }

  private setMenuItems(
    items: AnalyticsMenuItem[],
    companyId: number,
    appId: number
  ): void {
    this.menuItemsSubject.next(items);
    this.loaded = true;
    this.loadedCompanyId = companyId;
    this.loadedAppId = appId;
  }

  getDashboardMappings(
    companyId: number,
    appId: number
  ): Observable<DashboardMappingItem[]> {
    return new Observable<DashboardMappingItem[]>((observer) => {
      this.http
        .get<
          DashboardMappingResponse[]
        >(`${this.apiUrl}/Companies/${companyId}/Analytics/dashboards?appId=${appId}`)
        .subscribe({
          next: (items) => {
            observer.next(
              items.map((item) => ({
                id: item.id,
                dashboardId: item.dashboardId
              }))
            );
            observer.complete();
          },
          error: (error) => observer.error(error)
        });
    });
  }

  getDashboardEmbedUrl(
    companyId: number,
    dashboardId: string
  ): Observable<DashboardEmbedResponse> {
    return this.http.get<DashboardEmbedResponse>(
      `${this.apiUrl}/Companies/${companyId}/Analytics/embed/${dashboardId}`
    );
  }
}
